
export default function ExclusiveMarket() {
  const localTopics = [
    { icon: 'ri-file-list-3-line', text: 'Local code updates' },
    { icon: 'ri-government-line', text: 'Regional compliance reminders' },
    { icon: 'ri-cloud-windy-line', text: 'Weather-related system considerations' },
    { icon: 'ri-newspaper-line', text: 'Area business news' },
    { icon: 'ri-search-eye-line', text: 'Local inspection trends' },
    { icon: 'ri-community-line', text: 'Community events when appropriate' },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* One Company Per Market */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          <div>
            <p className="text-sm uppercase tracking-widest text-red-600 font-semibold mb-4">Exclusivity Policy</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              One Company Per Market.<br />No Exceptions.
            </h2>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed">
              Fire protection is local. So is trust.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              That's why Fireline Email Co. works with only <strong className="text-gray-900">one fire protection company per geographic service area.</strong> If you're a client, I will not write emails for your direct competitors in your market.
            </p>
            <div className="space-y-4">
              {[
                { label: 'No overlap.', desc: 'Your territory is protected.' },
                { label: 'No shared positioning.', desc: 'Your voice stays distinct.' },
                { label: 'No recycled messaging.', desc: 'Your strategy stays yours.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center mt-0.5">
                    <i className="ri-shield-check-line text-xl text-red-600"></i>
                  </div>
                  <p className="text-gray-700">
                    <strong className="text-gray-900">{item.label}</strong> {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="bg-gray-900 rounded-lg p-10 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-red-600 rounded-lg mb-6">
                <i className="ri-map-pin-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Your Market. Your Emails.</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                When you sign on, your service area is locked. No competitor in your region will ever receive the same strategy, positioning, or messaging.
              </p>
              <div className="border-t border-gray-700 pt-6">
                <p className="text-sm text-gray-400 italic">
                  "Your communication strategy stays yours — not shared, not recycled, not resold."
                </p>
              </div>
            </div>
            {/* Decorative accent */}
            <div className="absolute -bottom-4 -right-4 w-full h-full bg-red-600/10 rounded-lg -z-10"></div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 mb-24"></div>

        {/* Localized Content */}
        <div className="text-center mb-14">
          <p className="text-sm uppercase tracking-widest text-red-600 font-semibold mb-4">Hyper-Local Messaging</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Localized, Market-Specific Content
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Your emails won't just be industry-specific — they'll be <strong className="text-gray-900">region-specific.</strong>
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {localTopics.map((topic, i) => (
            <div
              key={i}
              className="bg-gray-50 rounded-lg p-6 flex items-center gap-4 hover:shadow-md transition-shadow"
            >
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg">
                <i className={`${topic.icon} text-2xl text-red-600`}></i>
              </div>
              <span className="text-gray-800 font-medium">{topic.text}</span>
            </div>
          ))}
        </div>

        {/* Why it matters callout */}
        <div className="bg-gray-50 rounded-lg p-12 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Why does this matter?</h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              A fire protection company in Arizona should not sound like one in Michigan.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Customers trust companies that clearly understand their local environment. Your emails will reflect your market — not generic national messaging.
            </p>
          </div>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-600 rounded-lg">
                <i className="ri-map-2-line text-xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Regional Relevance</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Content that speaks to your customers' actual environment, regulations, and concerns.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-600 rounded-lg">
                <i className="ri-heart-line text-xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Deeper Trust</h4>
                <p className="text-gray-600 text-sm leading-relaxed">Local knowledge signals expertise. Customers feel understood, not marketed to.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-600 rounded-lg">
                <i className="ri-trophy-line text-xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Competitive Edge</h4>
                <p className="text-gray-600 text-sm leading-relaxed">While competitors send generic blasts, your emails feel personal and locally informed.</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
